import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Bell, TrendingUp, TrendingDown, CheckCheck, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { format } from "date-fns";
import * as userApi from '../../services/userApi';

export default function NotificationQueue({
    notifications,
    unreadCount,
    onNotificationClick,
    isLoading,
    userId
}) {
    const queryClient = useQueryClient();
    const [markingRead, setMarkingRead] = React.useState(false);
    const [markingAllRead, setMarkingAllRead] = React.useState(false);

    const handleMarkAsRead = async (notificationId) => {
        setMarkingRead(true);
        await userApi.markNotificationRead(userId, notificationId);
        queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
        setMarkingRead(false);
    };

    const handleMarkAllAsRead = async () => {
        setMarkingAllRead(true);
        await userApi.markAllNotificationsRead(userId);
        queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
        setMarkingAllRead(false);
    };

    const handleNotificationClick = async (notification) => {
        if (!notification.is_read) {
            await handleMarkAsRead(notification.id);
        }
        onNotificationClick(notification);
    };

    return (
        <Sheet>
            <SheetTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-slate-400 hover:text-white hover:bg-slate-800"
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium">
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent className="w-96 bg-slate-950 border-slate-800 p-0">
                <SheetHeader className="p-4 border-b border-slate-800">
                    <div className="flex items-center justify-between">
                        <SheetTitle className="text-white flex items-center gap-2">
                            <Bell className="h-5 w-5" />
                            Notifications
                            {unreadCount > 0 && (
                                <Badge className="bg-red-500 text-white">{unreadCount}</Badge>
                            )}
                        </SheetTitle>
                        {unreadCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleMarkAllAsRead}
                                disabled={markingAllRead}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 text-xs"
                            >
                                {markingAllRead ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                    <CheckCheck className="h-3 w-3 mr-1" />
                                )}
                                Mark all read
                            </Button>
                        )}
                    </div>
                </SheetHeader>

                <ScrollArea className="h-[calc(100vh-80px)]">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                            <Bell className="h-12 w-12 mb-3 opacity-30" />
                            <p className="text-sm font-medium">No notifications yet</p>
                            <p className="text-xs text-slate-600 mt-1">Alerts will appear here in real-time</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-800">
                            {notifications.map((notification) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    onClick={handleNotificationClick}
                                />
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </SheetContent>
        </Sheet>
    );
}

function NotificationItem({ notification, onClick }) {
    // We'll need to fetch alert data to display signal/pair info
    // For now, show basic notification info
    return (
        <div
            onClick={() => onClick(notification)}
            className={cn(
                "p-4 cursor-pointer transition-colors",
                notification.is_read
                    ? "bg-transparent hover:bg-slate-900"
                    : "bg-slate-900/50 hover:bg-slate-800"
            )}
        >
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-blue-500/20">
                    <Bell className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium text-sm">
                            New Alert
                        </span>
                        {!notification.is_read && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                    </div>
                    <p className="text-slate-400 text-sm truncate">
                        Alert ID: {notification.alert_id}
                    </p>
                    <p className="text-slate-600 text-xs mt-1">
                        {format(new Date(notification.created_date), 'MMM d, HH:mm')}
                    </p>
                </div>
            </div>
        </div>
    );
}
